// COPIADO de src/domain/queueSignature.ts — não edite aqui; edite no gestor
/**
 * queueSignature.ts — `version` determinística da fila de reprodução.
 *
 * POR QUÊ: o receptor (kernel Android / navegador em TV Box) precisa saber se a
 * programação mudou SEM refazer o download. O backend expõe a fila com
 * `ETag: <version>` e devolve `304 Not Modified` quando o receptor manda
 * `If-None-Match`. Isso economiza banda e, mais importante, faz o receptor
 * continuar reproduzindo sem pausa.
 *
 * COMO: FNV-1a 32 bits (implementado aqui, ~10 linhas, ZERO dependência nova —
 * não vale acoplar o receptor a `crypto`, que não existe no browser de TV e tem
 * comportamento diferente entre Hermes, Hermes Android e JSC).
 *
 * REGRA CRÍTICA 1: o hash é calculado sobre o CONTEÚDO CANÔNICO
 * (itens ordenados por `adId`, mais o `screenId`), não sobre a ordem do array.
 * Assim uma mudança de ordem (rotação) NÃO invalida a `version` e não força novo
 * fetch. Se um dia isso mudar, todo receptor vira cliente que baixa a cada volta
 * do loop.
 *
 * REGRA CRÍTICA 2 (v2): o `screenId` ENTRA no conteúdo canônico. A `version` é o
 * ETag POR TELA (`GET /screens/:id/queue`, API-CONTRACT §4.1); se ela fosse
 * calculada só sobre os itens, duas telas com o mesmo conjunto de anúncios
 * compartilhariam a mesma `version` e qualquer cache compartilhado
 * (CDN/proxy/`If-None-Match` trocado) poderia servir a programação de uma TV
 * para outra — o modo de falha mais caro do produto ("a TV mostra anúncio de
 * outra loja"). Por isso o prefixo de esquema foi para `v2`: uma `version`
 * antiga, calculada sem o `screenId`, NÃO valida e não é aceita como igual.
 *
 * Colisão: 32 bits é suficiente para "mudou ou não mudou" entre o fetch atual e
 * o próximo. Não use isto como assinatura de segurança.
 */

import type { ID, PlaybackItem, PlaybackQueue } from './types';

/**
 * Prefixo de esquema: permite trocar a regra de hash no futuro sem ambiguidade.
 * `v2` = o `screenId` faz parte do conteúdo canônico (ver REGRA CRÍTICA 2).
 */
export const QUEUE_VERSION_PREFIX = 'v2';

/**
 * FNV-1a 32 bits -> 8 caracteres hexadecimais minúsculos.
 * Os operadores bitwise são intencionais (é aritmética de 32 bits, não manipulação
 * de flags) — daí os disables locais, em vez de desligar `no-bitwise` no repo.
 */
export function hashContent(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash ^= input.charCodeAt(i);
    // multiplicação por 16777619 com aritmética de 32 bits (sem BigInt)
    hash = Math.imul(hash, 0x01000193);
  }
  // `>>> 0` garante unsigned e padStart completa com zeros à esquerda.
  // eslint-disable-next-line no-bitwise
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Representação canônica do CONTEÚDO da fila de UMA tela: `screenId` + itens
 * ordenados por `adId`, com os campos que realmente afetam a reprodução (mídia,
 * tipo, duração).
 *
 * A URL NÃO entra porque URLs assinadas (S3/pre-signed) mudam sem a mídia mudar —
 * isso faria a `version` oscilar e o receptor baixar fila à toa.
 */
export function canonicalQueueContent(items: PlaybackItem[], screenId?: ID): string {
  const sorted = items
    .map(item => ({
      adId: item.adId,
      mediaId: item.media?.id ?? '',
      kind: item.media?.kind ?? '',
      durationSeconds: Math.round(item.durationSeconds),
    }))
    .sort((a, b) => (a.adId < b.adId ? -1 : a.adId > b.adId ? 1 : 0));

  return [
    // `s=` primeiro: a identidade da tela é o eixo do ETag (ver REGRA CRÍTICA 2).
    `s=${screenId ?? ''}`,
    `n=${sorted.length}`,
    ...sorted.map(
      item => `${item.adId}:${item.mediaId}:${item.kind}:${item.durationSeconds}`,
    ),
  ].join('|');
}

/**
 * `version` da fila: `v2-<hash>` calculada sobre o conteúdo canônico DA TELA.
 * Passe sempre o `screenId` — sem ele a version volta a ser global e colide
 * entre telas (é o bug que a v2 corrige).
 */
export function computeQueueVersion(items: PlaybackItem[], screenId?: ID): string {
  return `${QUEUE_VERSION_PREFIX}-${hashContent(canonicalQueueContent(items, screenId))}`;
}

/**
 * Verdadeiro se a `version` declarada na fila corresponde ao seu conteúdo.
 * O receptor usa no arranque para descartar uma fila corrompida em cache
 * (ex.: gravação interrompida no meio de um JSON) sem confiar no servidor.
 * Também discrimina `v1-*` (sem `screenId`) de `v2-*`: a versão antiga não valida.
 */
export function isQueueVersionValid(queue: PlaybackQueue): boolean {
  if (!queue || typeof queue.version !== 'string') {
    return false;
  }
  return queue.version === computeQueueVersion(queue.items ?? [], queue.screenId);
}

/** Comparação tolerante (trim) para ETag/If-None-Match. */
export function isSameQueueVersion(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) {
    return false;
  }
  return a.trim() === b.trim();
}

/** `true` quando chegou conteúdo novo (usado para decidir "aplicar agora"). */
export function queueVersionChanged(
  previous: string | undefined,
  next: string | undefined,
): boolean {
  if (!previous) {
    return true;
  }
  return !isSameQueueVersion(previous, next);
}
